import React from 'react';

import { SortableTable } from '~/components/core';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Tooltip from '~/components/core/Atomic/Tooltip';
import CounterWithRefresh from '~/components/core/CounterWithRefresh/CounterWithRefresh';
import EmptyState from '~/components/core/EmptyState';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { DownloadIcon } from '~/components/icons';
import { isoUtcDateTimeToLocal } from '~/DateTimeUtils';
import type { ConfigurationSnapshot, SnapshotListResponse } from '~/server_shared/openapi/data-contracts';

interface SnapshotsListProps {
  snapshotsData: SnapshotListResponse | null | undefined;
  onRefresh: () => void;
  isLoading?: boolean;
  onRowClick?: (snapshot: ConfigurationSnapshot) => void;
  selectedItemId?: string | number;
  onSortByColumn?: (columnId: string, order: 'asc' | 'desc') => void;
  defaultSortColumn?: string;
  defaultSortOrder?: 'asc' | 'desc';
}

const SnapshotsList: React.FC<SnapshotsListProps> = ({
  snapshotsData,
  onRefresh,
  isLoading = false,
  onRowClick,
  selectedItemId,
  onSortByColumn,
  defaultSortColumn = 'created_at',
  defaultSortOrder = 'desc',
}) => {
  // The endpoint redirects to a signed storage URL that sets the downloaded file's name.
  const handleDownload = React.useCallback((snapshot: ConfigurationSnapshot) => {
    window.open(`/sysconfig/api/v1/configuration_repository/snapshots/${snapshot.id}/data`, '_blank');
  }, []);

  const getActionCell = React.useCallback(
    (snapshot: ConfigurationSnapshot) => (
      <Tooltip title="Download snapshot">
        <IconButton onClick={() => handleDownload(snapshot)} size="small" aria-label="Download snapshot">
          <DownloadIcon size={16} />
        </IconButton>
      </Tooltip>
    ),
    [handleDownload]
  );

  // Row click handler
  const handleRowClick = React.useCallback(
    (id: number) => {
      const snapshot = snapshotsData?.snapshots.find((s) => s.id === id);
      if (snapshot && onRowClick) {
        onRowClick(snapshot);
      }
    },
    [snapshotsData?.snapshots, onRowClick]
  );

  // Sort handler with adapter pattern
  const handleSortByColumn = React.useCallback(
    (_event: React.MouseEvent, { id, order }: { id: string; order: 'asc' | 'desc' }) => {
      if (onSortByColumn) {
        onSortByColumn(id, order);
      }
    },
    [onSortByColumn]
  );

  const columns = React.useMemo(
    () => [
      {
        id: 'snapshot_name',
        label: 'Name',
        specialCell: ({ snapshot_name }: ConfigurationSnapshot) => snapshot_name || 'Unnamed Snapshot',
      },
      {
        id: 'created_at',
        label: 'Created',
        specialCell: ({ created_at }: ConfigurationSnapshot) => isoUtcDateTimeToLocal(created_at),
      },
      {
        id: 'created_by_username',
        label: 'Creator',
        specialCell: ({ created_by_username }: ConfigurationSnapshot) => created_by_username || 'System',
      },
      {
        id: 'description',
        label: 'Description',
        maxWidthPercentage: 30,
        overflow: true,
        specialCell: ({ description }: ConfigurationSnapshot) => description || '-',
      },
      {
        id: 'actions',
        label: '',
        disableSort: true,
        specialCell: getActionCell,
      },
    ],
    [getActionCell]
  );

  // Counter with refresh functionality
  const counterViewText = React.useMemo(
    () => (
      <CounterWithRefresh
        onRefresh={onRefresh}
        isLoading={isLoading}
        refreshTooltipText="Refresh snapshots"
        refreshAriaLabel="Refresh snapshots"
        pagination={{
          totalCount: snapshotsData?.snapshots?.length || 0,
          page: 0,
          rowsPerPage: snapshotsData?.snapshots?.length || 0,
          resultsTruncated: false,
        }}
      />
    ),
    [onRefresh, isLoading, snapshotsData?.snapshots?.length]
  );

  if (!snapshotsData) {
    // When isLoading is false and we still have no data, the fetch finished without
    // results — surface that as an error state so the loading span ends accurately
    // instead of running until the 30s cap. See NGTPA-24705.
    return <SkeletonTable isError={!isLoading} rowsCount={5} columnsCount={columns.length} />;
  }

  const snapshots = snapshotsData.snapshots;

  return (
    <SortableTable
      columns={columns}
      rows={snapshots}
      stickyHeader
      onRowClick={onRowClick ? handleRowClick : undefined}
      selectedRowsIds={selectedItemId ? [selectedItemId] : undefined}
      defaultOrderColumn={columns.findIndex((column) => column.id === defaultSortColumn)}
      order={defaultSortOrder}
      onSortByColumn={onSortByColumn ? handleSortByColumn : undefined}
      counterViewText={counterViewText}
      emptyStateComponent={<EmptyState subtitle="No snapshots created yet" />}
    />
  );
};

export default SnapshotsList;
